import React from "react";

/**
 * Note kinds and the helpers that classify a saved note.
 *
 * One session document is one note of one kind. These four labels are the
 * kinds you can add; the Notes section (PatientNotes.jsx) lists the notes and
 * owns every handler. The helpers here mirror the backend's
 * sessionNoteService.js, and both must agree or a note opens on the wrong
 * editor.
 */

const KINDS = [
  { key: "template", label: "Template", icon: "fa-light fa-file-waveform" },
  { key: "note", label: "Note", icon: "fa-light fa-clipboard-medical" },
  { key: "write", label: "Handwritten", icon: "fa-light fa-pen-nib" },
  { key: "media", label: "Media", icon: "fa-light fa-camera" },
];

const ICON_BY_KIND = KINDS.reduce((acc, k) => ({ ...acc, [k.key]: k.icon }), {});

/** Icon for a note's kind, falling back to the generic note glyph. */
export function iconForKind(kind) {
  return ICON_BY_KIND[kind] || "fa-light fa-clipboard-medical";
}

export const KIND_LABELS = {
  template: "Template",
  note: "Note",
  write: "Handwritten",
  media: "Media",
};

/**
 * Every text button on the Notes section shares this width, sized to the
 * longest label ("Complete appointment"), so Save, Save and invoice and
 * Complete appointment read as one matched set wherever they sit.
 */
export const NOTE_ACTION_WIDTH = 170;

/**
 * A note's kind, derived from its content when the document doesn't carry one.
 */
export function deriveKind(s) {
  if (s?.sessionType) return s.sessionType;
  if (s?.capturedValues && Object.keys(s.capturedValues).length) return "template";
  if (Array.isArray(s?.sections) && s.sections.length) return "template";
  if (s?.sessionsTemplateId) return "template";
  if (s?.notepadContent) return "note";
  if (s?.handwritingImage) return "write";
  if (Array.isArray(s?.mediaFiles) && s.mediaFiles.length) return "media";
  return "note";
}

/** True when a document predates the split and holds more than one kind. */
export function isLegacyBundle(s) {
  if (s?.sessionType) return false;
  const kinds = [
    (s?.capturedValues && Object.keys(s.capturedValues).length) ||
      (Array.isArray(s?.sections) && s.sections.length) ||
      !!s?.sessionsTemplateId,
    !!s?.notepadContent,
    !!s?.handwritingImage,
    Array.isArray(s?.mediaFiles) && s.mediaFiles.length > 0,
  ];
  return kinds.filter(Boolean).length > 1;
}

/**
 * Every kind of content a document actually holds. Mirrors contentKindsOf in
 * backend/src/services/sessionNoteService.js.
 */
export function contentKindsOf(s) {
  const kinds = [];
  if (
    (s?.capturedValues && Object.keys(s.capturedValues).length) ||
    (Array.isArray(s?.sections) && s.sections.length) ||
    s?.sessionsTemplateId
  ) kinds.push("template");
  if (s?.notepadContent) kinds.push("note");
  if (s?.handwritingImage) kinds.push("write");
  if (Array.isArray(s?.mediaFiles) && s.mediaFiles.length) kinds.push("media");
  return kinds;
}

/**
 * List rows for one saved note.
 *
 * A legacy bundle (written before one-note-one-kind, holding e.g. a template
 * AND a handwritten page) becomes one row PER kind it holds, so both are
 * reachable and neither disappears when the other is opened. Each row still
 * points at the same document via `realId`; nothing is split in the database.
 *
 * @param row  either a list-endpoint row (carries `contentKinds`) or a full
 *             session document (kinds derived locally).
 */
export function expandNoteRows(row, { title } = {}) {
  const kinds = Array.isArray(row?.contentKinds) && row.contentKinds.length
    ? row.contentKinds
    : contentKindsOf(row);
  const name = title || noteTitleOf(row);
  const id = String(row._id);

  // Written after the split, or holding at most one kind: a single row.
  if (row?.sessionType || kinds.length <= 1) {
    return [{
      ...row,
      _id: id,
      realId: id,
      sessionType: row?.sessionType || kinds[0] || deriveKind(row),
      sessionsNoteTitle: name,
      isSplit: false,
    }];
  }

  return kinds.map((kind) => ({
    ...row,
    // Synthetic id: the list needs distinct keys, the editor uses `realId`.
    _id: `${id}#${kind}`,
    realId: id,
    sessionType: kind,
    sessionsNoteTitle: KIND_LABELS[kind] || name,
    isSplit: true,
  }));
}

/** Display name for a note: stored title, else its kind. */
export function noteTitleOf(s) {
  const stored = typeof s?.sessionsNoteTitle === "string" ? s.sessionsNoteTitle.trim() : "";
  if (stored) return stored;
  return KIND_LABELS[deriveKind(s)] || KIND_LABELS.note;
}

/**
 * "Add note": the kind picker for a new note, as the last row of each sitting
 * in the Notes section, so where a note will go is where the control is.
 *
 * It expands in place rather than opening a popup menu: the row opens into
 * a short list of options (SyncTo first when enabled, then the kinds), and
 * picking one adds that note and closes the list. The caller owns `open`, so
 * only one sitting's list is open at a time.
 */
export const ADD_OPTION_CLASS = "acc-notes-add-option";

export function AddNoteExpander({
  open = false,
  onToggle,
  onAdd,
  disabled = false,
  leadingItems = null,
  label = "Add note",
  id = "add-note-toggle",
}) {
  return (
    <div className="acc-notes-add w-100">
      <button
        type="button"
        id={id}
        className={`acc-notes-add-toggle${open ? " is-open" : ""}`}
        onClick={() => onToggle?.(!open)}
        disabled={disabled}
        aria-expanded={open}
        aria-controls={`${id}-options`}
      >
        <i className={`fa-solid ${open ? "fa-minus" : "fa-plus"} me-2`} />
        {label}
      </button>
      {open && (
        <div className="acc-notes-add-options" id={`${id}-options`} role="group" aria-label={label}>
          {leadingItems}
          {KINDS.map((k) => (
            <button
              key={k.key}
              type="button"
              className={ADD_OPTION_CLASS}
              onClick={() => onAdd?.(k.key)}
            >
              <i className={k.icon} />
              <span>{k.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export { KINDS };
